import {
  formatMediaDuration,
  getResourceThumbnailUrl,
  groupResourcesByDate,
  isVideoResource,
} from '../resource/resourceViewUtils';
import { truncateName } from './maps';

export const MAX_GALLERY_ARTWORKS = 32;

const FRAME_WIDTH = 3;
const FRAME_HEIGHT = 2;
const FRAME_GAP_X = 2;
const FRAME_X0 = 9;
const FRAME_Y = 1;
const CORRIDOR_HEIGHT = 9;
const MIN_CORRIDOR_WIDTH = 25;

const safeResourceId = (resource, index) => String(resource?.id ?? `gallery-${index}`);

/**
 * 把项目资源转换成真正可行走的画廊地图。画作只保存轻量描述，图片由引擎按视口懒加载。
 */
export function buildResourceGalleryScene({
  projectId,
  projectName,
  resources,
  state = 'ready',
}) {
  const visibleResources = (Array.isArray(resources) ? resources : [])
    .slice(0, MAX_GALLERY_ARTWORKS);
  const groups = groupResourcesByDate(visibleResources);
  const flattened = groups.flatMap((group) => (
    group.resources.map((resource, index) => ({
      resource,
      groupLabel: index === 0 ? group.label : '',
    }))
  ));
  const frameStride = FRAME_WIDTH + FRAME_GAP_X;
  const corridorWidth = Math.max(
    MIN_CORRIDOR_WIDTH,
    FRAME_X0 + Math.max(1, flattened.length) * frameStride + 7,
  );
  const rows = Array.from({ length: CORRIDOR_HEIGHT }, (_, ty) => (
    Array.from({ length: corridorWidth }, (_, tx) => (
      ty === 0 || ty === CORRIDOR_HEIGHT - 1 || tx === 0 || tx === corridorWidth - 1 ? '#' : '.'
    ))
  ));
  const put = (tx, ty, value) => {
    if (rows[ty]?.[tx] != null) rows[ty][tx] = value;
  };

  const labels = [];
  const hotspots = [];
  const artworks = [];

  flattened.forEach(({ resource, groupLabel }, index) => {
    const tx = FRAME_X0 + index * frameStride;
    const ty = FRAME_Y;
    const resourceId = safeResourceId(resource, index);

    for (let dy = 0; dy < FRAME_HEIGHT; dy += 1) {
      for (let dx = 0; dx < FRAME_WIDTH; dx += 1) put(tx + dx, ty + dy, 'G');
    }

    if (groupLabel) {
      labels.push({
        x: tx + 1,
        y: 0.28,
        text: `${groupLabel}`,
        small: true,
      });
    }

    artworks.push({
      id: resourceId,
      tx,
      ty,
      widthTiles: FRAME_WIDTH,
      heightTiles: FRAME_HEIGHT,
      thumbnailUrl: getResourceThumbnailUrl(resource),
      isVideo: isVideoResource(resource),
      duration: formatMediaDuration(resource?.duration),
      name: resource?.name || '未命名作品',
    });
    hotspots.push({
      tx: tx + 1,
      ty: FRAME_Y + FRAME_HEIGHT,
      range: 1.55,
      label: `调查作品 · ${truncateName(resource?.name || '未命名作品', 12)}`,
      action: { type: 'custom', id: 'gallery-resource', resourceId },
    });
  });

  // 入口在走廊左端，完整资源中心位于右端，整个场景只沿水平方向探索。
  put(3, CORRIDOR_HEIGHT - 2, 'q');
  put(corridorWidth - 4, CORRIDOR_HEIGHT - 2, '1');
  labels.push(
    { x: 3, y: CORRIDOR_HEIGHT - 2.82, text: '返回项目', small: true },
    { x: corridorWidth - 4, y: CORRIDOR_HEIGHT - 2.82, text: '完整资源中心', small: true },
  );
  const mats = [
    {
      tx: 3,
      ty: CORRIDOR_HEIGHT - 2,
      label: '离开资源画廊',
      action: { type: 'custom', id: 'gallery-exit' },
    },
    {
      tx: corridorWidth - 4,
      ty: CORRIDOR_HEIGHT - 2,
      label: '进入完整资源中心',
      action: { type: 'custom', id: 'gallery-resource-center' },
    },
  ];

  if (flattened.length === 0) {
    labels.push({
      x: Math.floor(corridorWidth / 2) - 0.5,
      y: FRAME_Y + 1.2,
      text: state === 'loading' ? '正在布展…' : state === 'error' ? '布展暂时中断' : '画廊还没有作品',
    });
  }

  const shortProjectName = truncateName(projectName || '当前项目', 12);
  return {
    id: `project-${projectId}:resource-gallery:${state === 'loading' ? 'loading' : 'corridor-v2'}`,
    grid: rows.map((row) => row.join('')),
    labels,
    mats,
    hotspots,
    artworks,
    spawn: { x: 4.5, y: CORRIDOR_HEIGHT - 2.3, dir: 'right' },
    crumb: `《${shortProjectName}》 · 资源画廊`,
    npcs: [
      {
        name: '阿展 · 策展',
        x: 6.2,
        y: CORRIDOR_HEIGHT - 2.35,
        activity: '整理近期作品',
        wanderRadius: 0.8,
        stationary: flattened.length === 0,
        paletteIndex: 4,
        lines: state === 'loading'
          ? ['作品还在布展，稍等一下就能沿着展墙参观。']
          : flattened.length > 0
            ? [`这里陈列最近 ${flattened.length} 件作品，沿走廊向右参观，走近后可以调查。`, '墙面只负责陈列，打开调查页后才会加载原图或视频。']
            : ['这里还没有作品，右侧出口可以进入完整资源中心。'],
      },
    ],
    ambience: 'local',
  };
}
